import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, X } from "lucide-react";
import { expensesApi, isFeatureOff } from "@/lib/billsNative";
import { ExpenseCard } from "@/components/expenses/ExpenseCard";
import { ExpenseForm } from "@/components/expenses/ExpenseForms";

const todayIso = () => new Date().toISOString().slice(0, 10);
const errText = (d) => d?.message || d?.error_code || "Something went wrong. Try again.";

export default function ExpensesHome() {
  const today = todayIso();
  const [day, setDay] = useState(today);
  const [meta, setMeta] = useState(null);
  const [sheet, setSheet] = useState(false);
  const [state, setState] = useState({ phase: "loading", expenses: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, phase: s.expenses.length ? "refreshing" : "loading" }));
    const ex = await expensesApi.list({ business_date: day, limit: 200 });
    if (isFeatureOff(ex)) return setState({ phase: "off", expenses: [] });
    if (!ex.ok && (ex.status === 503 || ex.status === 0)) return setState({ phase: "unavailable", expenses: [] });
    if (!ex.ok && ex.data?.error_code === "outlet_scope_required") return setState({ phase: "no-outlet", expenses: [] });
    setState({ phase: ex.ok ? "ready" : "error", expenses: ex.ok ? ex.data.expenses : [] });
  }, [day]);

  useEffect(() => { expensesApi.meta().then((r) => r.ok && setMeta(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = (fn) => async (body, reset) => {
    setBusy(true); setError("");
    const r = await fn(body);
    setBusy(false);
    if (!r.ok) return setError(errText(r.data));
    reset?.();
    setSheet(false);
    if (body.business_date !== day) setDay(body.business_date); else load();
  };

  const onVoid = async (e) => {
    const reason = window.prompt(`Reverse ${e.currency} ${e.amount}? Give a reason — the record is kept, never deleted.`);
    if (!reason?.trim()) return;
    setBusy(true);
    const r = await expensesApi.void(e.expense_id, reason.trim());
    setBusy(false);
    if (!r.ok) return setError(errText(r.data));
    load();
  };

  const cashTotal = state.expenses
    .filter((e) => e.status === "ACTIVE" && e.cash_classification === "CASH")
    .reduce((a, e) => a + Number(e.amount), 0);

  return (
    <div className="min-h-screen bg-archival-base pb-28" data-testid="expenses-home">
      <header className="sticky top-0 z-30 border-b border-archival bg-archival-surface">
        <div className="mx-auto flex h-14 max-w-[720px] items-center gap-3 px-4">
          <Link to="/bills" data-testid="expenses-back" className="text-archival-secondary"><ArrowLeft size={18} /></Link>
          <div className="leading-tight">
            <p className="text-[15px] font-semibold text-archival-primary">Expenses</p>
            <p className="text-[11px] text-archival-muted">Recorded facts · Accounts does the reconciliation</p>
          </div>
        </div>
        <div className="mx-auto flex max-w-[720px] items-center gap-2 px-4 pb-3">
          <span data-testid="expenses-tab-expenses"
            className="inline-flex h-9 items-center rounded-full bg-archival-primary px-3 text-[13px] font-medium text-white">
            Expenses
          </span>
          <input type="date" max={today} value={day} onChange={(e) => setDay(e.target.value)} data-testid="expenses-day-filter"
            className="ml-auto h-9 rounded-md border border-[#E4E0D8] bg-white px-2 text-[13px]" />
        </div>
      </header>

      <main className="mx-auto max-w-[720px] px-4 py-4">
        {state.phase === "off" && <p data-testid="expenses-feature-off" className="text-[14px] text-archival-secondary">Expenses are not enabled for this environment.</p>}
        {state.phase === "unavailable" && <p data-testid="expenses-unavailable" className="text-[14px] text-archival-secondary">Expenses are temporarily unavailable. Nothing was recorded.</p>}
        {state.phase === "no-outlet" && <p data-testid="expenses-no-outlet" className="text-[14px] text-amber-800">Select an outlet first — expenses are recorded per outlet.</p>}
        {state.phase === "loading" && <Loader2 data-testid="expenses-loading" size={18} className="animate-spin text-archival-muted" />}

        {["ready", "refreshing", "error"].includes(state.phase) && (
          <>
            <div data-testid="expenses-cash-summary" className="mb-3 rounded-lg border border-[#E4E0D8] bg-white p-3">
              <p className="text-[11px] uppercase tracking-wide text-archival-muted">Cash paid out on {day}</p>
              <p className="text-[22px] font-semibold tabular-nums text-archival-primary">₹{cashTotal.toFixed(2)}</p>
              <p className="text-[11px] text-archival-muted">Sum of today&apos;s cash expenses only.</p>
            </div>
            {state.expenses.length === 0
              ? <p data-testid="expenses-empty" className="text-[14px] text-archival-secondary">No expenses recorded for this day.</p>
              : <ul data-testid="expenses-list" className="space-y-2">
                  {state.expenses.map((e) => <ExpenseCard key={e.expense_id} expense={e} onVoid={onVoid} busy={busy} />)}
                </ul>}
          </>
        )}

        {error && <p data-testid="expenses-error" className="mt-3 rounded bg-red-50 px-2 py-1 text-[12px] text-red-800">{error}</p>}
      </main>

      {["ready", "refreshing", "error"].includes(state.phase) && (
        <button type="button" data-testid="expenses-add-btn" onClick={() => { setError(""); setSheet(true); }}
          style={{ backgroundColor: "var(--action)" }}
          className="fixed bottom-6 left-1/2 z-40 inline-flex h-12 -translate-x-1/2 items-center gap-2 rounded-full px-5 text-[15px] font-semibold text-white shadow-xl ring-1 ring-black/15 hover:brightness-110 transition-[filter]">
          <Plus size={16} /> Add expense
        </button>
      )}

      {sheet && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/30" data-testid="expenses-sheet">
          <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-archival-base p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-[15px] font-semibold text-archival-primary">Record expense</p>
              <button type="button" data-testid="expenses-sheet-close" onClick={() => setSheet(false)} className="text-archival-secondary"><X size={18} /></button>
            </div>
            <ExpenseForm meta={meta} today={today} busy={busy} error={error} onSubmit={submit(expensesApi.create)} />
          </div>
        </div>
      )}
    </div>
  );
}
